'use client'

import { createContext, type ReactNode, useContext } from 'react'

/** The counts a pagination summary reads; `total` is null when unknown. */
export type PaginationSummaryCounts =
  | { mode: 'cursor'; shown: number; total: number | null; hasMore: boolean }
  | { mode: 'offset'; from: number; to: number; total: number | null }

/**
 * Every word the kit's primitives show. The package carries no message
 * catalogs: the admin app reads these English defaults, and the web app
 * provides its translations through `UiStringsProvider`.
 */
export type UiStrings = {
  /** Screen-reader name of the close button on a dialog or sheet. */
  close: string
  sidebar: {
    /** The scrim that closes the off-canvas panel. */
    close: string
    /** The sidebar trigger and rail. */
    toggle: string
  }
  /** `CommandDialog`'s hidden title and description, unless passed. */
  command: { title: string; description: string }
  /** `DataErrorBoundary`'s fallback; its title unless passed. */
  dataError: { title: string; description: string; retry: string }
  offline: string
  /** `MinimalAreaChart`'s words and its default count format. */
  chart: {
    /** Shown when a window has no data, unless passed. */
    empty: string
    /** The plot's accessible name while it loads. */
    loading: string
    /** The legend's accessible name. */
    series: string
    /** A plotted value, in the reader's number format, unless passed. */
    count: (value: number) => string
  }
  pagination: {
    /** "Showing …" in the reader's language, numbers formatted. */
    summary: (counts: PaginationSummaryCounts) => string
    /** Beside the ← → keys. */
    keyboardHint: string
    rows: string
    previousPage: string
    nextPage: string
  }
}

/** English numbers, as the admin app (English only) reads them. */
const ENGLISH_NUMBER_LOCALE = 'en-US'
const englishNumbers = new Intl.NumberFormat(ENGLISH_NUMBER_LOCALE)

function englishCount(value: number): string {
  return englishNumbers.format(value)
}

function englishPaginationSummary(counts: PaginationSummaryCounts): string {
  if (counts.mode === 'cursor') {
    if (counts.total !== null) {
      return `Showing ${englishCount(counts.shown)} of ${englishCount(counts.total)}`
    }
    return counts.hasMore
      ? `Showing ${englishCount(counts.shown)} (more available)`
      : `Showing ${englishCount(counts.shown)}`
  }
  const range = `${englishCount(counts.from)}–${englishCount(counts.to)}`
  return counts.total === null
    ? `Showing ${range}`
    : `Showing ${range} of ${englishCount(counts.total)}`
}

export const DEFAULT_UI_STRINGS: UiStrings = {
  close: 'Close',
  sidebar: { close: 'Close sidebar', toggle: 'Toggle sidebar' },
  command: {
    title: 'Command palette',
    description: 'Search for a command to run…',
  },
  dataError: {
    title: 'This section failed to load',
    description: 'You can retry without reloading the whole page.',
    retry: 'Retry',
  },
  offline: "You're offline. Changes will retry when you're back online.",
  chart: {
    empty: 'No data in this window.',
    loading: 'Loading chart',
    series: 'Chart series',
    count: englishCount,
  },
  pagination: {
    summary: englishPaginationSummary,
    keyboardHint: 'to page',
    rows: 'Rows',
    previousPage: 'Previous page',
    nextPage: 'Next page',
  },
}

const UiStringsContext = createContext<UiStrings>(DEFAULT_UI_STRINGS)

/**
 * Gives the primitives below it their words. Pass a stable (memoized)
 * object: every primitive that reads it re-renders when it changes.
 */
export function UiStringsProvider({
  strings,
  children,
}: {
  strings: UiStrings
  children: ReactNode
}) {
  return (
    <UiStringsContext.Provider value={strings}>
      {children}
    </UiStringsContext.Provider>
  )
}

/** The words for the primitives here; English outside a provider. */
export function useUiStrings(): UiStrings {
  return useContext(UiStringsContext)
}
